import { useEffect, useState } from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/videa-components/src/index';
import { Checkbox } from '@repo/videa-components/src/ui/checkbox';
import { Input } from '@repo/videa-components/src/ui/input';
import { useInView } from 'react-intersection-observer';

import { useGetPractices } from '../../../react-query/useGetPractices';
import { useDebounce } from '../../../utils/hooks/useDebounce';

export const PracticeTable = ({
  selectedPractices,
  setSelectedPractices,
}: {
  selectedPractices: string[];
  setSelectedPractices: (selectedPractices: string[]) => void;
}) => {
  const [search, setSearch] = useState('');

  const { ref: bottomOfPageRef, inView: isBottomPracticeInView } = useInView();

  const { debouncedValue: debouncedSearch } = useDebounce(search);

  const { data: practices, fetchNextPage } = useGetPractices({
    practice: { name: debouncedSearch },
  });

  const { nextCursor } = practices?.pages[practices.pages.length - 1] || {};

  useEffect(() => {
    if (nextCursor && isBottomPracticeInView && practices?.pages.length) {
      fetchNextPage();
    }
  }, [
    fetchNextPage,
    nextCursor,
    practices?.pages.length,
    isBottomPracticeInView,
  ]);

  return (
    <div className="vai-flex vai-flex-col vai-gap-6 vai-w-full">
      <span className="vai-typography-large vai-text-zinc-800">
        Add Practices to Region
      </span>
      <div className="vai-flex vai-flex-row vai-items-center vai-justify-between">
        <Input
          className="[&&]:vai-w-1/4 vai-box-border vai-divide-none"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search for a practice"
          dataTestId="practice-table-search-input"
        />
        <span className="vai-typography-muted">
          {selectedPractices.length} Practices Added
        </span>
      </div>
      <div className="vai-flex vai-flex-col vai-gap-6 vai-h-[calc(100vh-32rem)]">
        <div className="vai-relative vai-w-full vai-overflow-auto vai-border vai-border-gray-200 vai-rounded-md">
          <Table>
            <TableHeader className="vai-sticky vai-top-0 vai-z-10 vai-bg-neutral-100">
              <TableRow>
                <TableHead className="vai-w-8">
                  <Checkbox disabled={true} />
                </TableHead>
                <TableHead>Practice</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="vai-overflow-y-auto vai-bg-white">
              {practices?.pages
                .flatMap((page) => page.data)
                .map((practice) => (
                  <TableRow key={practice.id} className="vai-h-14">
                    <TableCell>
                      <Checkbox
                        checked={selectedPractices?.includes(practice.id)}
                        dataTestId={`practice-table-checkbox-${practice.id}`}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedPractices([
                              ...selectedPractices,
                              practice.id,
                            ]);
                          } else {
                            setSelectedPractices(
                              selectedPractices.filter(
                                (id) => id !== practice.id,
                              ),
                            );
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>{practice.name}</TableCell>
                  </TableRow>
                ))}
              {practices && <div ref={bottomOfPageRef} />}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};
